// Platform-level admin overview. Complements AdminEventDashboard,
// which is per-event; this page aggregates across the platform:
// event counts by status, the site donation total, per-event
// campaign totals for events that have a donation module enabled,
// and a recent-events list ordered by updatedAt.

import { api } from "@/lib/api-client";
import type { Event } from "@/lib/event-schema";
import { ArrowUpRight, Heart, Loader2, Plus, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

type CampaignTotal = {
  slug: string;
  title: string;
  totalCents: number;
  goalCents?: number;
  /** True when the totals endpoint failed; lets the UI distinguish a
   *  legitimate $0 raised from a transient fetch error. */
  fetchFailed?: boolean;
};

type SiteTotal = { ok: true; totalCents: number } | { ok: false };

type DashboardData = {
  events: Event[];
  /** Event docs that failed to load (counted so we can warn the admin). */
  failedEventFetches: number;
  siteTotal: SiteTotal;
  campaigns: CampaignTotal[];
};

type LoadState =
  | { status: "loading" }
  | { status: "ready"; data: DashboardData }
  | { status: "error"; message: string };

export function AdminDashboard() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const summaries = await api.events.list();
        // Fetching each full doc lets us read modules.donation and
        // updatedAt without changing the existing events:list endpoint.
        // At expected platform scale (a handful of events) this is
        // cheap; revisit when it crosses ~50. Run the site-total fetch
        // in parallel with the per-event GETs so a slow donation
        // endpoint doesn't gate the rest of the page.
        const eventDocsPromise = Promise.all(
          summaries.map((s) =>
            api.events.get(s.slug).catch((err) => {
              console.error("dashboard: failed to load event", { slug: s.slug, err });
              return null;
            }),
          ),
        );
        const sitePromise = api.donate.totals.site().catch((err) => {
          console.warn("dashboard: site total fetch failed", err);
          return null;
        });
        const [events, siteRaw] = await Promise.all([eventDocsPromise, sitePromise]);
        const failedEventFetches = events.filter((e) => e === null).length;
        const loaded = events.filter((e): e is Event => e !== null);

        // Per-event campaign totals. Only events with an enabled
        // donation module count as active campaigns. Tighten to
        // strict-equality on `true` so a future schema with a wider
        // `enabled` union doesn't silently include unintended events.
        const campaigns = await Promise.all(
          loaded
            .filter((e) => e.modules.donation?.enabled === true)
            .map(async (e) => {
              try {
                const t = await api.donate.totals.event(e.slug, e.slug);
                return {
                  slug: e.slug,
                  title: e.title,
                  totalCents: t.totalCents,
                  goalCents: e.modules.donation?.goalCents,
                };
              } catch (err) {
                console.warn("dashboard: campaign total fetch failed", {
                  slug: e.slug,
                  err,
                });
                return {
                  slug: e.slug,
                  title: e.title,
                  totalCents: 0,
                  goalCents: e.modules.donation?.goalCents,
                  fetchFailed: true,
                };
              }
            }),
        );

        if (cancelled) return;
        setState({
          status: "ready",
          data: {
            events: loaded,
            failedEventFetches,
            siteTotal: siteRaw ? { ok: true, totalCents: siteRaw.totalCents } : { ok: false },
            campaigns,
          },
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Failed to load overview.",
        });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="grid h-96 place-items-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <p className="text-sm text-red-600 dark:text-red-400">{state.message}</p>
      </div>
    );
  }

  const { events, failedEventFetches, siteTotal, campaigns } = state.data;
  const published = events.filter((e) => e.status === "published").length;
  const drafts = events.filter((e) => e.status === "draft").length;
  // Sort by updatedAt descending; cap the list at 6 to keep the
  // overview scannable on a single screen.
  const recent = [...events].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 6);

  return (
    <div className="mx-auto w-full max-w-5xl">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-accent">Platform</p>
          <h1 className="display-title mt-2 text-[clamp(2rem,4vw,3rem)] leading-tight">Overview</h1>
          <p className="mt-1 text-sm text-muted">
            Where every event and the general fund stand right now.
          </p>
        </div>
        <Link
          to="/admin/events/new"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-sm font-medium text-canvas hover:bg-ink/90 dark:bg-accent dark:text-accent-on shadow-glow"
        >
          <Plus className="h-4 w-4" strokeWidth={1.75} />
          New event
        </Link>
      </header>

      {failedEventFetches > 0 ? (
        <p
          role="alert"
          className="mb-6 rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-700 dark:text-red-400"
        >
          {failedEventFetches} {failedEventFetches === 1 ? "event" : "events"} could not be loaded.
          Counts and lists below reflect only the events that succeeded. Try refreshing, or check
          the browser console for details.
        </p>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          eyebrow="Published"
          value={String(published)}
          hint={published === 1 ? "event live" : "events live"}
        />
        <StatCard eyebrow="Drafts" value={String(drafts)} hint="in progress" muted />
        <StatCard
          eyebrow="General fund"
          icon={<Heart className="h-4 w-4" strokeWidth={1.75} />}
          value={siteTotal.ok ? `$${(siteTotal.totalCents / 100).toLocaleString()}` : "Unavailable"}
          hint={siteTotal.ok ? "raised site-wide" : "could not load total"}
          unavailable={!siteTotal.ok}
        />
      </section>

      <section className="mt-12">
        <header className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-xl font-light text-ink">Active campaigns</h2>
          <p className="text-xs text-subtle">
            {campaigns.length === 0
              ? "No event has a donation module enabled yet."
              : `${campaigns.length} ${campaigns.length === 1 ? "campaign" : "campaigns"}`}
          </p>
        </header>
        {campaigns.length === 0 ? (
          <p className="surface-card p-6 text-sm text-muted">
            Enable the donation module on an event editor to see its campaign total here.
          </p>
        ) : (
          <div className="grid gap-3">
            {campaigns.map((c) => (
              <CampaignRow key={c.slug} campaign={c} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <header className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-xl font-light text-ink">Recent edits</h2>
          <Link to="/admin" className="text-xs text-muted hover:text-accent transition-colors">
            All events →
          </Link>
        </header>
        {recent.length === 0 ? (
          <p className="surface-card p-6 text-sm text-muted">
            No events yet. Start by creating one.
          </p>
        ) : (
          <div className="grid gap-2">
            {recent.map((e) => (
              <RecentEventRow key={e.slug} event={e} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({
  eyebrow,
  value,
  hint,
  icon,
  muted = false,
  unavailable = false,
}: {
  eyebrow: string;
  value: string;
  hint: string;
  icon?: React.ReactNode;
  muted?: boolean;
  /** Renders the value at smaller size and dim color when the data
   *  source returned an error rather than a real zero. */
  unavailable?: boolean;
}) {
  const valueClass = unavailable
    ? "mt-3 font-display text-lg font-light text-muted leading-none"
    : muted
      ? "mt-3 font-display text-3xl font-light text-muted leading-none"
      : "mt-3 font-display text-3xl font-light text-ink leading-none";
  return (
    <article className="surface-card p-5">
      <p className="eyebrow text-accent inline-flex items-center gap-2">
        {icon}
        {eyebrow}
      </p>
      <p className={valueClass}>{value}</p>
      <p className="mt-1 text-xs text-subtle">{hint}</p>
    </article>
  );
}

function CampaignRow({ campaign }: { campaign: CampaignTotal }) {
  const raised = campaign.totalCents / 100;
  const goal = campaign.goalCents ? campaign.goalCents / 100 : null;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : null;
  return (
    <Link
      to={`/admin/events/${encodeURIComponent(campaign.slug)}/dashboard`}
      className="surface-card group flex flex-wrap items-center gap-4 p-4 hover:border-accent/60 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg font-light text-ink truncate">{campaign.title}</p>
        {campaign.fetchFailed ? (
          <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">
            Could not load total. Open the campaign for details.
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-muted">
            ${raised.toLocaleString()}{" "}
            {goal ? (
              <>
                raised of <span className="text-ink">${goal.toLocaleString()}</span>
                {pct !== null ? ` (${pct}%)` : null}
              </>
            ) : (
              "raised so far"
            )}
          </p>
        )}
        {goal && !campaign.fetchFailed ? (
          // biome-ignore lint/a11y/useFocusableInteractive: progressbar is a display widget, not a keyboard-interactive control
          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line/60"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct ?? 0}
            aria-label={`${campaign.title}: ${pct ?? 0}% of fundraising goal`}
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-accent/70 to-accent"
              style={{ width: `${pct ?? 0}%` }}
            />
          </div>
        ) : null}
      </div>
      <ArrowUpRight
        className="h-4 w-4 text-muted group-hover:text-accent transition-colors"
        strokeWidth={1.75}
      />
    </Link>
  );
}

function RecentEventRow({ event }: { event: Event }) {
  const updated = new Date(event.updatedAt);
  // Locale formatting can vary by deploy environment; keep it short and
  // predictable instead of expressive.
  const updatedLabel = updated.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return (
    <Link
      to={`/admin/events/${encodeURIComponent(event.slug)}`}
      className="surface-card group flex items-center gap-4 p-4 hover:border-accent/60 transition-colors"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent-ink dark:text-accent">
        <Sparkles className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink truncate">{event.title}</p>
        <p className="text-xs text-muted">
          {event.status === "published" ? "Live" : "Draft"} · Updated {updatedLabel}
        </p>
      </div>
      <ArrowUpRight
        className="h-4 w-4 text-muted group-hover:text-accent transition-colors"
        strokeWidth={1.75}
      />
    </Link>
  );
}
